/* ========================================
   COLLECTION LOAD MORE
   Replaces the numbered pagination on the collection page:

   - "Load more" fetches the next page with the Section Rendering API and appends
     the product cards to the grid that is already on screen (no page reload).
   - The URL follows the products in view: it moves forward as pages are loaded
     and back to ?page=1 when the visitor scrolls up to the first page again.
   - Landing directly on ?page=N (refresh, shared link, back button) back-fills
     pages 1..N-1 above the server-rendered page, so the visitor sees the same
     N pages of products they had before.
   ======================================== */

(function () {
  'use strict';

  /** Pages fetched at the same time while back-filling (browser allows ~6 per domain). */
  const MAX_PARALLEL_REQUESTS = 4;
  const REQUEST_TIMEOUT = 10000;
  const CARD_SELECTOR = 'product-card, .product-card';
  const PROMO_BANNER_CLASS = 'promotional-banner';

  const LoadMoreUtils = {
    /**
     * Build a collection URL for `page`, preserving sort/filter params already in the address bar.
     * @param {number} page
     * @param {string} [sectionId] - renders through the Section Rendering API when set
     * @param {boolean} [keepPageOne] - keep an explicit page=1 instead of dropping the param
     */
    buildPageUrl(page, sectionId, keepPageOne) {
      const url = new URL(window.location.href);
      if (page > 1 || keepPageOne) {
        url.searchParams.set('page', String(page));
      } else {
        url.searchParams.delete('page');
      }
      if (sectionId) {
        url.searchParams.set('section_id', sectionId);
      }
      return url.pathname + url.search;
    },

    async fetchDocument(url) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      try {
        const response = await fetch(url, {
          signal: controller.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'text/html,application/xhtml+xml'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const html = await response.text();
        return new DOMParser().parseFromString(html, 'text/html');
      } finally {
        clearTimeout(timeoutId);
      }
    },

    /**
     * Product cards of a fetched page, in grid order.
     *
     * Two kinds of non-product items live in the grid and are re-rendered on every
     * page, so they are dropped to avoid duplicates:
     *   - grid insertions (section blocks) - always kept from the page the server rendered
     *   - promotional banners (styled collection, after the 2nd and 6th product) - only
     *     meaningful once, at the top of the grid, so they are kept from page 1 alone
     */
    getCardsFromDocument(doc, options) {
      const keepPromotionalBanners = !!(options && options.keepPromotionalBanners);
      const list = doc.querySelector('.collection__main product-list, .collection__main .product-list, product-list, .product-list');
      if (!list) return [];

      return Array.from(list.children).filter((element) => {
        if (!element.classList) return true;
        if (element.classList.contains('grid-insertion')) return false;
        if (!keepPromotionalBanners && element.classList.contains(PROMO_BANNER_CLASS)) return false;
        return true;
      });
    },

    /** Run `task` over `items` with a small concurrency cap, keeping the results in order. */
    async mapWithConcurrency(items, task, limit) {
      const results = new Array(items.length);
      let cursor = 0;

      const worker = async () => {
        while (cursor < items.length) {
          const index = cursor++;
          results[index] = await task(items[index], index);
        }
      };

      const workers = [];
      for (let i = 0; i < Math.min(limit, items.length); i++) {
        workers.push(worker());
      }
      await Promise.all(workers);

      return results;
    }
  };

  class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
      // Test / dual-render mode: markup can sit in the DOM (often hidden) without
      // running backfill, scroll sync, or click handlers until activate() is called.
      if (this.hasAttribute('data-defer-init')) return;
      this.init();
    }

    /**
     * Start load-more behaviour. Safe to call from the console after showing
     * a deferred [data-collection-load-more-test-wrap] block.
     */
    activate() {
      this.removeAttribute('data-defer-init');
      this.init();
    }

    init() {
      if (this._initialized) return;
      this._initialized = true;

      this.button = this.querySelector('[data-load-more-button]');
      this.shownCountElement = this.querySelector('[data-load-more-shown]');
      this.loading = false;

      // First card of each loaded page, ascending: drives which page the URL shows.
      this.pageGroups = [];
      this.urlPage = Math.max(1, parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10));

      if (this.button) {
        this.button.addEventListener('click', this.onButtonClick.bind(this));
      }

      // The OR-filter system paginates its merged results client side, so it
      // owns the page rendering: the button only re-triggers its next link.
      if (this.dataset.mode !== 'or') {
        this.registerPageGroup(this.currentPage, this.productList && this.productList.querySelector(CARD_SELECTOR));
        // Products carrying the "hide" tag are dropped from the grid, so the
        // rendered cards are the source of truth for the count.
        this.updateShownCount();
        this.backfillPreviousPages();
      }
    }

    disconnectedCallback() {
      this.stopScrollSync();
    }

    get sectionId() {
      return this.dataset.sectionId || '';
    }

    /** Highest page loaded into the grid (not necessarily the one in view). */
    get currentPage() {
      return Math.max(1, parseInt(this.dataset.currentPage || '1', 10));
    }

    set currentPage(page) {
      this.dataset.currentPage = String(page);
    }

    get totalPages() {
      return Math.max(1, parseInt(this.dataset.totalPages || '1', 10));
    }

    get pageSize() {
      return Math.max(1, parseInt(this.dataset.pageSize || '12', 10));
    }

    get totalItems() {
      return parseInt(this.dataset.totalItems || '0', 10);
    }

    get productList() {
      if (this._productList && this._productList.isConnected) return this._productList;

      const section = this.closest('.shopify-section') || document;
      this._productList =
        (this.sectionId && section.querySelector(`#product-list-${CSS.escape(this.sectionId)}`)) ||
        section.querySelector('.collection__main product-list') ||
        section.querySelector('.collection__main .product-list') ||
        section.querySelector('product-list');

      return this._productList;
    }

    onButtonClick(event) {
      event.preventDefault();

      if (this.dataset.mode === 'or') {
        // Hand back to the OR-filter pagination handler (collection-filter.js).
        const nextLink = this.querySelector('a[rel="next"]');
        if (nextLink) nextLink.click();
        return;
      }

      this.loadNextPage();
    }

    async loadNextPage() {
      if (this.loading) return;

      const nextPage = this.currentPage + 1;
      if (nextPage > this.totalPages) {
        this.setExhausted();
        return;
      }

      this.setLoading(true);

      try {
        const doc = await LoadMoreUtils.fetchDocument(LoadMoreUtils.buildPageUrl(nextPage, this.sectionId));
        const cards = LoadMoreUtils.getCardsFromDocument(doc);

        if (cards.length === 0) {
          this.setExhausted();
          return;
        }

        const firstCard = this.insertCards(cards, 'append');
        this.registerPageGroup(nextPage, firstCard);
        this.currentPage = nextPage;
        this.syncUrl(nextPage);
        this.refreshState(doc);
        this.announce(cards.length);
      } catch (error) {
        console.error('[CollectionLoadMore] Failed to load page:', error);
        // Let the visitor still reach the products if the request keeps failing.
        window.location.href = LoadMoreUtils.buildPageUrl(nextPage);
      } finally {
        this.setLoading(false);
      }
    }

    /**
     * Direct load of ?page=N: fetch pages 1..N-1 and put them above the products
     * the server rendered, so a refresh restores every page the visitor had open.
     */
    async backfillPreviousPages() {
      const targetPage = this.currentPage;
      if (targetPage < 2 || this.dataset.backfilled === 'true') return;
      if (!this.productList) return;

      this.dataset.backfilled = 'true';
      this.setLoading(true);

      if (typeof window.LoadingManager !== 'undefined' && window.LoadingManager.showCollectionLoading) {
        window.LoadingManager.showCollectionLoading();
      }

      const pages = [];
      for (let page = 1; page < targetPage; page++) {
        pages.push(page);
      }

      try {
        const pageCards = await LoadMoreUtils.mapWithConcurrency(
          pages,
          async (page) => {
            try {
              const doc = await LoadMoreUtils.fetchDocument(LoadMoreUtils.buildPageUrl(page, this.sectionId));
              return LoadMoreUtils.getCardsFromDocument(doc, { keepPromotionalBanners: page === 1 });
            } catch (error) {
              console.error(`[CollectionLoadMore] Failed to back-fill page ${page}:`, error);
              return [];
            }
          },
          MAX_PARALLEL_REQUESTS
        );

        // Page 1 carries the promotional banners for the top of the grid, so the copies
        // the server rendered further down (page N's own) are dropped.
        const hasIncomingBanners = pageCards.some((cards) => {
          return (cards || []).some((card) => card.classList && card.classList.contains(PROMO_BANNER_CLASS));
        });

        if (hasIncomingBanners) {
          this.productList.querySelectorAll('.' + PROMO_BANNER_CLASS).forEach((banner) => banner.remove());
        }

        // Insert every page before the products the server rendered, keeping page order.
        const anchor = this.productList.querySelector(CARD_SELECTOR);
        let added = 0;

        pageCards.forEach((cards, index) => {
          if (!cards || cards.length === 0) return;
          const firstCard = this.insertCards(cards, 'prepend', anchor);
          this.registerPageGroup(pages[index], firstCard);
          added += cards.length;
        });

        if (added > 0) this.announce(added);
      } finally {
        this.setLoading(false);
        this.updateShownCount();

        if (typeof window.LoadingManager !== 'undefined' && window.LoadingManager.hideCollectionLoading) {
          window.LoadingManager.hideCollectionLoading();
        }
      }
    }

    /**
     * @param {'append'|'prepend'} position
     * @param {Element} [anchor] - node to insert before when prepending
     * @returns {Element|null} first inserted card
     */
    insertCards(cards, position, anchor) {
      const list = this.productList;
      if (!list) return null;

      const fragment = document.createDocumentFragment();
      cards.forEach((card) => fragment.appendChild(document.importNode(card, true)));

      const firstCard = fragment.firstElementChild;

      if (position === 'prepend') {
        // Keep grid insertions (banners) where the section rendered them.
        const before = anchor && anchor.isConnected ? anchor : list.querySelector(CARD_SELECTOR);
        if (before) {
          list.insertBefore(fragment, before);
        } else {
          list.appendChild(fragment);
        }
      } else {
        list.appendChild(fragment);
      }

      this.updateShownCount();

      return firstCard;
    }

    /** Remember where a page starts in the grid so scrolling can map back to it. */
    registerPageGroup(page, card) {
      if (!card) return;

      this.pageGroups = this.pageGroups.filter((group) => group.page !== page);
      this.pageGroups.push({ page, card });
      this.pageGroups.sort((a, b) => a.page - b.page);

      if (this.pageGroups.length > 1) this.startScrollSync();
    }

    startScrollSync() {
      if (this._scrollHandler) return;

      this._scrollHandler = () => {
        if (this._scrollTicking) return;
        this._scrollTicking = true;
        window.requestAnimationFrame(() => {
          this._scrollTicking = false;
          this.syncUrlToScrollPosition();
        });
      };

      window.addEventListener('scroll', this._scrollHandler, { passive: true });
      window.addEventListener('resize', this._scrollHandler, { passive: true });
    }

    stopScrollSync() {
      if (!this._scrollHandler) return;

      window.removeEventListener('scroll', this._scrollHandler);
      window.removeEventListener('resize', this._scrollHandler);
      this._scrollHandler = null;
    }

    /** Page whose products the visitor is looking at: the last one that has entered the viewport. */
    getPageInView() {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      let page = this.pageGroups.length ? this.pageGroups[0].page : 1;

      for (let i = 0; i < this.pageGroups.length; i++) {
        const group = this.pageGroups[i];
        if (!group.card || !group.card.isConnected) continue;
        if (group.card.getBoundingClientRect().top >= viewportHeight) break;
        page = group.page;
      }

      return page;
    }

    syncUrlToScrollPosition() {
      if (this.pageGroups.length < 2) return;

      const page = this.getPageInView();
      if (page === this.urlPage) return;

      this.syncUrl(page);
    }

    syncUrl(page) {
      // Once more than one page is in play the parameter stays explicit, so
      // scrolling back to the top reads as page=1 rather than a bare URL.
      const url = LoadMoreUtils.buildPageUrl(page, null, this.pageGroups.length > 1);
      const state = Object.assign({}, window.history.state || {}, { collectionPage: page });

      // replaceState (not pushState) so "back" leaves the collection instead of
      // stepping through every page the visitor loaded.
      window.history.replaceState(state, '', url);
      this.urlPage = page;
    }

    /** Refresh paging state from the page we just fetched (totals can change while browsing). */
    refreshState(doc) {
      const fetched = doc.querySelector('collection-load-more');

      if (fetched) {
        if (fetched.dataset.totalPages) this.dataset.totalPages = fetched.dataset.totalPages;
        if (fetched.dataset.totalItems) this.dataset.totalItems = fetched.dataset.totalItems;
        if (fetched.dataset.nextUrl) {
          this.dataset.nextUrl = fetched.dataset.nextUrl;
        } else {
          delete this.dataset.nextUrl;
        }
      }

      if (this.currentPage >= this.totalPages) {
        this.setExhausted();
      }
    }

    updateShownCount() {
      if (!this.shownCountElement || !this.productList) return;

      this.shownCountElement.textContent = String(this.productList.querySelectorAll(CARD_SELECTOR).length);
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.classList.toggle('is-loading', isLoading);

      if (this.button) {
        this.button.disabled = isLoading;
        this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
    }

    setExhausted() {
      if (this.button) this.button.remove();
      this.button = null;
      delete this.dataset.nextUrl;
    }

    announce(addedCount) {
      const detail = { addedCount, currentPage: this.currentPage, totalPages: this.totalPages };

      this.dispatchEvent(new CustomEvent('collection:products:appended', { bubbles: true, detail }));
      // Existing collection scripts re-observe lazy images on this event.
      document.dispatchEvent(new CustomEvent('filterContentLoaded', { detail }));
    }
  }

  if (!window.customElements.get('collection-load-more')) {
    window.customElements.define('collection-load-more', CollectionLoadMore);
  }

  window.CollectionLoadMore = CollectionLoadMore;
  window.CollectionLoadMoreUtils = LoadMoreUtils;

  /** Activate every deferred collection-load-more on the page (test mode helper). */
  window.activateCollectionLoadMore = function activateCollectionLoadMore() {
    document.querySelectorAll('collection-load-more[data-defer-init]').forEach((el) => {
      if (typeof el.activate === 'function') el.activate();
    });
  };
})();
